<template>
  <sg-tooltip
    ref="popper"
    v-model:visible="showPicker"
    :show-arrow="false"
    :fallback-placements="['bottom', 'top', 'right', 'left']"
    :offset="0"
    :gpu-acceleration="false"
    :popper-class="`sg-color-picker__panel sg-color-dropdown`"
    :stop-popper-mouse-event="false"
    theme="light"
    trigger="click"
    transition="sg-zoom-in-top"
    persistent
  >
    <template #content>
      <div v-click-outside="hide">
        <div class="sg-color-dropdown__main-wrapper">
          <sv-panel ref="svPanel" :color="color" />
          <hue-slider ref="hue" class="hue-slider" :color="color" vertical />
        </div>
        <alpha-slider v-if="showAlpha" ref="alpha" :color="color" />
        <predefine
          v-if="predefine"
          ref="predefine"
          :color="color"
          :colors="predefine"
        />
        <div class="sg-color-dropdown__btns">
          <span class="sg-color-dropdown__value">
            <sg-input
              v-model="customInput"
              :validate-event="false"
              size="small"
              @keyup.enter="handleConfirm"
              @on-blur="handleConfirm"
            />
          </span>
          <sg-button
            size="small"
            type="primary"
            text
            class="sg-color-dropdown__link-btn"
            @click="clear"
          >
            {{ t('sg.colorpicker.clear') }}
          </sg-button>
          <sg-button
            type="primary"
            size="small"
            class="sg-color-dropdown__btn"
            @click="confirmValue"
          >
            {{ t('sg.colorpicker.confirm') }}
          </sg-button>
        </div>
      </div>
    </template>
    <template #default>
      <div
        :class="[
          'sg-color-picker',
          colorDisabled ? 'is-disabled' : '',
          colorSize ? `sg-color-picker--${colorSize}` : '',
        ]"
      >
        <div v-if="colorDisabled" class="sg-color-picker__mask"></div>
        <div class="sg-color-picker__trigger" @click="handleTrigger">
          <span
            class="sg-color-picker__color"
            :class="{ 'is-alpha': showAlpha }"
          >
            <span
              class="sg-color-picker__color-inner"
              :style="{
                backgroundColor: displayedColor,
              }"
            >
              <sg-icon
                v-show="(modelValue && modelValue!=='transparent') || showPanelColor"
                class="sg-color-picker__icon is-icon-arrow-down"
              >
                <CaretBottomSolid />
              </sg-icon>
              <sg-icon
                v-if="(!modelValue || modelValue==='transparent') && !showPanelColor"
                class="sg-color-picker__empty is-icon-close"
              >
                <Close />
              </sg-icon>
            </span>
          </span>
        </div>
      </div>
    </template>
  </sg-tooltip>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  nextTick,
  onMounted,
  provide,
  reactive,
  ref,
  watch,
} from 'vue'
import debounce from 'lodash/debounce'
import SgButton from '@sgui-plus/components/button'
import SgIcon from '@sgui-plus/components/icon'
import { ClickOutside } from '@sgui-plus/directives'
import { useLocale, useSize, useFormItem } from '@sgui-plus/hooks'
import SgTooltip from '@sgui-plus/components/tooltip'
import SgInput from '@sgui-plus/components/input'
import { UPDATE_MODEL_EVENT } from '@sgui-plus/utils/constants'
import { isValidComponentSize } from '@sgui-plus/utils/validators'
import { debugWarn } from '@sgui-plus/utils/error'
import { Close,CaretBottomSolid } from '@sgui-plus/icons-vue'
import AlphaSlider from './components/alpha-slider.vue'
import HueSlider from './components/hue-slider.vue'
import Predefine from './components/predefine.vue'
import SvPanel from './components/sv-panel.vue'
import Color from './color'
import { OPTIONS_KEY } from './useOption'

import type { PropType } from 'vue'
import type { ComponentSize } from '@sgui-plus/utils/size'
import type { IUseOptions } from './useOption'

export default defineComponent({
  name: 'SgColorPicker',
  components: {
    SgButton,
    SgTooltip,
    SgInput,
    SgIcon,
    Close,
    CaretBottomSolid,
    SvPanel,
    HueSlider,
    AlphaSlider,
    Predefine,
  },
  directives: {
    ClickOutside,
  },
  props: {
    modelValue: String,
    showAlpha: Boolean,
    colorFormat: String,
    disabled: Boolean,
    size: {
      type: String as PropType<ComponentSize>,
      validator: isValidComponentSize,
    },
    predefine: {
      type: Array
    },
    clearType: {
      type: String,
      default: 'null'
    },
    validateEvent: {
      type: Boolean,
      default: true
    }
  },
  emits: ['on-change', 'on-active-change', UPDATE_MODEL_EVENT, 'on-visible-change'],
  setup(props, { emit }) {
    const { t } = useLocale()
    const { formItem } = useFormItem()

    const hue = ref()
    const svPanel = ref()
    const alpha = ref()
    const popper = ref(null)
    // data
    const color = reactive(
      new Color({
        enableAlpha: props.showAlpha,
        format: props.colorFormat  as string,
        value: props.modelValue,
        clearType: props.clearType
      })
    )
    const showPicker = ref(false)
    const showPanelColor = ref(false)
    const customInput = ref('')
    // computed
    const displayedColor = computed(() => {
      if (!props.modelValue && !showPanelColor.value) {
        return 'transparent'
      }
      if (props.modelValue == 'transparent' && !showPanelColor.value) {
        return 'transparent'
      }
      return displayedRgb(color, props.showAlpha)
    })
    const colorSize = useSize()
    const colorDisabled = computed(() => {
      return props.disabled
    })

    const currentColor = computed(() => {
      return !props.modelValue && !showPanelColor.value ? '' : color.value
    })
    // watch
    watch(
      () => props.modelValue,
      (newVal) => {
        if (!newVal || newVal == 'transparent') {
          showPanelColor.value = false
        } else if (newVal && newVal !== color.value) {
          color.fromString(newVal)
        }
      }
    )

    watch(
      () => currentColor.value,
      (val) => {
        customInput.value = val
        emit('on-active-change', val)
      }
    )

    watch(
      () => props.clearType,
      (val) => {
        color.clearType = val
      }
    )

    watch(
      () => color.value,
      () => {
        if (props.clearType == 'transparent' && color.value == 'transparent') return
        if (!props.modelValue && !showPanelColor.value) {
          showPanelColor.value = true
        }
      }
    )

    watch(
      () => props.colorFormat,
      (val) => {
        color.set('format', val, true)
        resetColor()
        nextTick(() => {
          confirmValue()
        })
      }
    )

    watch(
      () => showPicker.value,
      (val) => {
        if (val == false && props.modelValue == 'transparent') {
          showPanelColor.value = false
        }
        emit('on-visible-change', val)
      }
    )

    // methods
    function displayedRgb(color, showAlpha) {
      if (!(color instanceof Color)) {
        throw Error('color should be instance of _color Class')
      }

      const { r, g, b } = color.toRgb()
      return showAlpha
        ? `rgba(${r}, ${g}, ${b}, ${color.get('alpha') / 100})`
        : `rgb(${r}, ${g}, ${b})`
    }

    function setShowPicker(value) {
      showPicker.value = value
    }

    const debounceSetShowPicker = debounce(setShowPicker, 100)

    function hide() {
      debounceSetShowPicker(false)
      resetColor()
    }

    function resetColor() {
      nextTick(() => {
        if (props.modelValue) {
          color.fromString(props.modelValue)
        } else {
          showPanelColor.value = false
        }
      })
    }

    function handleTrigger() {
      if (colorDisabled.value) return
      debounceSetShowPicker(!showPicker.value)
    }

    function handleConfirm() {
      color.fromString(customInput.value)
    }

    function confirmValue() {
      let value: string|null = color.value
      if (props.clearType !== 'transparent' && color.value == 'transparent') {
        value = null
      }
      emit(UPDATE_MODEL_EVENT, value)
      emit('on-change', value)
      if (props.validateEvent) {
        formItem?.validate('change').catch((err) => debugWarn(err))
      }
      debounceSetShowPicker(false)
      // check if modelValue change, if not change, then reset color.
      nextTick(() => {
        const newColor = new Color({
          enableAlpha: props.showAlpha,
          format: props.colorFormat as string,
          value: props.modelValue,
          clearType: props.clearType
        })
        if (!color.compare(newColor)) {
          resetColor()
        }
      })
    }

    function clear() {
      debounceSetShowPicker(false)
      if (props.clearType == 'transparent') {
        emit(UPDATE_MODEL_EVENT, 'transparent')
        emit('on-change', 'transparent')
      } else {
        emit(UPDATE_MODEL_EVENT, null)
        emit('on-change', null)
      }
      if (props.modelValue !== null && props.validateEvent) {
        formItem?.validate('change').catch((err) => debugWarn(err))
      }
      resetColor()
    }

    onMounted(() => {
      if (props.modelValue) {
        customInput.value = currentColor.value
      }
    })
    watch(
      () => showPicker.value,
      () => {
        nextTick(() => {
          hue.value?.update()
          svPanel.value?.update()
          alpha.value?.update()
        })
      }
    )

    provide<IUseOptions>(OPTIONS_KEY, {
      currentColor,
    })

    return {
      color: color as Color,
      colorDisabled,
      colorSize,
      displayedColor,
      showPanelColor,
      showPicker,
      customInput,
      handleConfirm,
      hide,
      handleTrigger,
      clear,
      confirmValue,
      t,
      hue,
      svPanel,
      alpha,
      popper,
    }
  },
})
</script>
